import React from 'react';
import {useLocation} from 'react-router-dom';
import {Alert, Button} from 'reactstrap';
import {EyeOff, Telescope} from 'lucide-react';
import {useAuthStateManager} from '../../hooks/useAuthStateManager';
import RoleNames from '../../lib/RoleNames';
import {RoutePaths} from '../../RoutePaths';
import {useResourceAllocation} from '../../resource_allocation/context/ResourceAllocationContext.tsx';
import {expandScenarioTagIds} from '../../calc_engine/transform/scenario-projection';
import {
    ProjectCertainty,
    ProjectLifecycle,
    ProjectStatus,
    ProjectVisibility
} from '../../types';

/**
 * The page-wide lens banner (mounted once, in Layout) — one slot, two states:
 *
 * - Lens ACTIVE: persistent amber warning stating exactly what's been widened
 *   into the numbers, so a what-if can never be mistaken for the published
 *   picture. In scenario mode (tags selected) it names the scenario and
 *   counts the tagged projects it re-admits.
 * - Lens OFF but the viewer can see draft / non-disclosed / tentative projects
 *   the published picture EXCLUDES: a quieter info banner in the same spot —
 *   the reminder that you may have unpublished work, pipeline work waiting on
 *   a decision, or confidential projects others can't see — with a one-click
 *   "View in What-if".
 *
 * Counts only projects already in the viewer's state — no information leak.
 * Copy note: drafts are UNPUBLISHED work in preparation (possibly fully
 * confirmed) — never describe them as "not real".
 */
const joinParts = (parts: string[]): string =>
    parts.length <= 2
        ? parts.join(' and ')
        : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

const plural = (count: number, noun: string): string =>
    `${count} ${noun}${count === 1 ? '' : 's'}`;

// The only pages that render calc-engine numbers the lens can change — the
// dashboard (grid + utilisation) and the Insights/Explore views. Management
// pages (Projects, Teams, People), docs, settings and the public pages show
// no lens-affected numbers, so the banner would just be noise there.
const NUMBER_PAGE_PREFIXES = [
    RoutePaths.dashboard(),
    RoutePaths.insights.root()
];

export const WhatIfBanner: React.FC = () => {
    const {isAuthenticated, isInRole} = useAuthStateManager();
    const {state, whatIfLens, setWhatIfLens} = useResourceAllocation();
    const {pathname} = useLocation();

    const isNumberPage = NUMBER_PAGE_PREFIXES.some(
        prefix => pathname === prefix || pathname.startsWith(`${prefix}/`)
    );

    if (
        !isNumberPage ||
        !isAuthenticated ||
        !isInRole(RoleNames.CustomerUser)
    ) {
        return null;
    }

    const projects = Object.values(state.projects);
    const draftCount = projects.filter(
        p => p.status === ProjectStatus.Draft
    ).length;
    const nonDisclosedCount = projects.filter(
        p => p.visibility === ProjectVisibility.NonDisclosed
    ).length;
    const tentativeCount = projects.filter(
        p => p.certainty === ProjectCertainty.Tentative
    ).length;
    const onHoldCount = projects.filter(
        p => p.lifecycle === ProjectLifecycle.OnHold
    ).length;
    const cancelledCount = projects.filter(
        p => p.lifecycle === ProjectLifecycle.Cancelled
    ).length;

    // Cancellation what-if: excluded projects drop out in BOTH modes, named
    // here so the banner always states the full lens.
    const excludedProjectIds = whatIfLens?.excludedProjectIds ?? [];
    const excludedNames = excludedProjectIds
        .map(id => state.projects[id]?.name)
        .filter((name): name is string => Boolean(name))
        .map(name => `'${name}'`);
    const exclusionPhrase =
        excludedProjectIds.length > 0
            ? `exclude ${plural(excludedProjectIds.length, 'project')}${
                  excludedNames.length > 0
                      ? ` (${excludedNames.slice(0, 3).join(', ')}${
                            excludedNames.length > 3 ? ', …' : ''
                        })`
                      : ''
              }`
            : null;

    // Any active lens shows an alternate reality — but edits are not
    // sandboxed. Say so, every time, right where the numbers changed.
    const realDataWarning = (
        <span className="small">
            Edits made in this view still change the real data for everyone.
        </span>
    );

    // Scenario mode — a named what-if: the published picture plus exactly
    // the tagged projects (category toggles are ignored by the projection).
    const scenarioTagIds = whatIfLens?.scenarioTagIds ?? [];
    if (whatIfLens && scenarioTagIds.length > 0) {
        const tagNames = scenarioTagIds
            .map(id => state.tags[id]?.name)
            .filter((name): name is string => Boolean(name))
            .map(name => `'${name}'`);
        // Count the projects the scenario re-admits (same expansion as the
        // projection, so this always matches the numbers on screen).
        const scenarioTagSet = expandScenarioTagIds(
            scenarioTagIds,
            state.tags
        );
        const taggedProjectIds = new Set(
            Object.values(state.projectTags)
                .filter(pt => scenarioTagSet.has(pt.tagId))
                .map(pt => pt.projectId)
        );
        const readmittedCount = projects.filter(
            p =>
                taggedProjectIds.has(p.id) &&
                !(
                    p.status === ProjectStatus.Published &&
                    p.visibility === ProjectVisibility.Disclosed &&
                    p.certainty === ProjectCertainty.Confirmed &&
                    (p.lifecycle ?? ProjectLifecycle.Active) ===
                        ProjectLifecycle.Active
                )
        ).length;

        return (
            <Alert
                color="warning"
                className="mb-0 rounded-0 py-2 d-flex align-items-center gap-2 flex-wrap"
            >
                <Telescope size={16} />
                <span>
                    <strong>
                        What-if scenario {joinParts(tagNames) || 'view'}
                    </strong>: the published picture plus{' '}
                    {plural(readmittedCount, 'tagged project')}
                    {exclusionPhrase ? `; ${exclusionPhrase}` : ''}.{' '}
                    {realDataWarning}
                </span>
                <Button
                    color="warning"
                    size="sm"
                    outline
                    className="ms-auto"
                    onClick={() => setWhatIfLens(null)}
                >
                    Back to published view
                </Button>
            </Alert>
        );
    }

    // Lens active — the amber what-if warning. Include-parts and the
    // exclusion phrase compose: either may be present alone.
    if (whatIfLens) {
        const parts: string[] = [];
        if (whatIfLens.includeDraft) {
            parts.push(plural(draftCount, 'unpublished draft project'));
        }
        if (whatIfLens.includeNonDisclosed) {
            parts.push(plural(nonDisclosedCount, 'non-disclosed project'));
        }
        if (whatIfLens.includeTentative) {
            parts.push(plural(tentativeCount, 'tentative project'));
        }
        if (whatIfLens.includeOnHold) {
            parts.push(plural(onHoldCount, 'on-hold project'));
        }
        if (whatIfLens.includeCancelled) {
            parts.push(plural(cancelledCount, 'cancelled project'));
        }

        const clauses: string[] = [];
        if (parts.length > 0) {
            clauses.push(`include ${joinParts(parts)}`);
        }
        if (exclusionPhrase) {
            clauses.push(exclusionPhrase);
        }

        return (
            <Alert
                color="warning"
                className="mb-0 rounded-0 py-2 d-flex align-items-center gap-2 flex-wrap"
            >
                <Telescope size={16} />
                <span>
                    <strong>What-if view</strong>: these numbers{' '}
                    {clauses.join(', and ')}. {realDataWarning}
                </span>
                <Button
                    color="warning"
                    size="sm"
                    outline
                    className="ms-auto"
                    onClick={() => setWhatIfLens(null)}
                >
                    Back to published view
                </Button>
            </Alert>
        );
    }

    // Lens off — the quieter excluded-work reminder in the same slot.
    // On-hold projects join the reminder (paused work is easy to forget);
    // cancelled ones deliberately do NOT — being permanently out of the
    // numbers is their point, and a perpetual "N cancelled projects are
    // excluded" would be noise. They stay reachable via the lens toggle.
    const excludedParts: string[] = [];
    if (draftCount > 0) {
        excludedParts.push(
            `${plural(draftCount, 'draft project')} (in preparation)`
        );
    }
    if (nonDisclosedCount > 0) {
        excludedParts.push(plural(nonDisclosedCount, 'non-disclosed project'));
    }
    if (tentativeCount > 0) {
        excludedParts.push(plural(tentativeCount, 'tentative project'));
    }
    if (onHoldCount > 0) {
        excludedParts.push(plural(onHoldCount, 'on-hold project'));
    }
    if (excludedParts.length === 0) {
        return null;
    }

    const viewInWhatIf = () => {
        setWhatIfLens({
            includeDraft: draftCount > 0,
            includeNonDisclosed: nonDisclosedCount > 0,
            includeTentative: tentativeCount > 0,
            includeOnHold: onHoldCount > 0
        });
    };

    const excludedTotal =
        draftCount + nonDisclosedCount + tentativeCount + onHoldCount;

    return (
        <Alert
            color="info"
            className="mb-0 rounded-0 py-2 d-flex align-items-center gap-2 flex-wrap"
        >
            <EyeOff size={16} />
            <span>
                {joinParts(excludedParts)}{' '}
                {excludedTotal === 1 ? 'is' : 'are'} excluded from these
                numbers.
            </span>
            <Button
                color="primary"
                size="sm"
                className="ms-auto"
                onClick={viewInWhatIf}
            >
                View in What-if
            </Button>
        </Alert>
    );
};

export default WhatIfBanner;
